import { EditIcon, Switch } from '@hc/ui/atoms';
import { MasterTable, MasterTopContainer } from '@hc/ui/components/backoffice';
import { useLabTests } from '@hc/store/backoffice/masters/labTests';
import { LabTestsDrawer, TableSkeletonLoader } from '@hc/ui/components';
import { getDateFormatToString } from '@hc/dayjs';
import { Pagination } from '@hc/ui/components/backoffice/pagination';
import { queryClient } from '@hc/utils';
import { Box, Grid, Stack } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { labTests_Style } from './style';

export default function LabTestsMaster(props) {
  const { ...rest } = props;

  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Store Data

  const {
    labTestData,
    labTestCount,
    getAllLabTests,
    labTestsStatusUpdate,
    initialEditLabTests,
    isIamValidToSave,
    addEditLabTests,
    clearlabTestData,
    loading,
  } = useLabTests((state) => ({
    labTestData: state.labTestData,
    labTestCount: state.labTestCount,
    getAllLabTests: state.getAllLabTests,
    labTestsStatusUpdate: state.labTestsStatusUpdate,
    initialEditLabTests: state.initialEditLabTests,
    isIamValidToSave: state.isIamValidToSave,
    addEditLabTests: state.addEditLabTests,
    clearlabTestData: state.clearlabTestData,
    loading: state.loading,
  }));
  //  ----- TABLE DATA SEARCH ---------
  const handleSearch = async (searchValue) => {
    setSearch(searchValue);
    await getAllLabTests(searchValue, rowsPerPage);
    setOffset(0);
  };

  // ------TABLE DATA SWITCH --------
  const onStatusChange = async (val) => {
    const payload = {
      id: val.id ?? 0,
      active_status: !val?.is_active ?? true,
    };
    const response = await labTestsStatusUpdate(payload);
    if (response === 200) {
      queryClient.invalidateQueries({
        queryKey: ['getAllLabTests', search, offset],
      });
      await getAllLabTests(search, rowsPerPage, offset);
    }
  };

  // ------PAGINATION ------------
  const onLimitChange = async (limit, offset) => {
    setRowsPerPage(limit);
    setOffset(offset);
    queryClient.invalidateQueries({
      queryKey: ['getAllLabTests', search, offset],
    });
    await getAllLabTests(search, limit, offset);
  };

  const onOffsetchange = async (offset) => {
    setOffset(offset);
    await getAllLabTests(search, rowsPerPage, offset);
  };

  // ADD
  const upsertMaster = async () => {
    const result = isIamValidToSave();
    if (result) {
      const response = await addEditLabTests();
      if (response?.status?.code === 200) {
        setDrawerOpen(false);
        clearlabTestData();

        queryClient.invalidateQueries({
          queryKey: ['getAllLabTests', search, offset],
        });
        await getAllLabTests(search, rowsPerPage, offset);
      }
    }
  };

  const handleAddClick = () => {
    setDrawerOpen(true);
  };
  const closeDrugMasterDrawer = () => {
    setDrawerOpen(false);
    clearlabTestData();
  };
  const onEdit = (val) => {
    initialEditLabTests(val);
    setDrawerOpen(true);
  };

  //  TABLE COLUMNS META DATA
  const columns = [
    {
      field: 'id',
      headerName: 'Id',
      width: 60,
    },
    {
      field: 'lab_test',
      headerName: 'Lab Tests',
      width: 180,
    },
    {
      field: 'status',
      headerName: 'Active/Inactive',
      width: 150,
      renderCell: (params) => (
        <Switch
          id="labTestStatus"
          checked={params?.row?.status}
          onChange={() => onStatusChange(params?.row?.value)}
          rootStyle={labTests_Style.switchSx}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 200,
      valueGetter: (params) =>
        getDateFormatToString(params.row.created_at, 'lll'),
    },
    {
      field: 'updated_at',
      headerName: 'Updated At',
      width: 200,
      valueGetter: (params) =>
        getDateFormatToString(params.row.updated_at, 'lll'),
    },
    {
      field: 'edit',
      headerName: 'Edit',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ pl: 1 }} onClick={() => onEdit(params?.row?.value)}>
          <EditIcon id='editLabTest' style={{ cursor: 'pointer', color: '#007965' }} />
        </Box>
      ),
    },
  ];

  //  INITIAL DATA FETCH

  const initialData = async () => {
    await getAllLabTests('');
  };

  useEffect(() => {
    initialData();
  }, []);

  return (
    <Box
      {...rest}
      p={2}
      sx={{
        height: '100vh',
        overflowY: 'scroll',
        backgroundColor: '#fff',
        borderRadius: '10px',
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <MasterTopContainer
            isMasterTitleShow
            isTitileShow={false}
            handleSearch={handleSearch}
            handleAddClick={handleAddClick}
            title="Lab Tests"
            count={labTestCount}
            showSearchField
            searchFieldPlaceholder="Search Lab Tests by name"
            showAddButton
            addButtonName="Add Lab Tests"
            addMasterStyle={{
              boxShadow:
                '0px 3px 1px -2px rgba(0,0,0,0.2),0px 3px 3px 0px rgba(0,0,0,0.14),0px 1px 2px 0px rgba(0,0,0,0.12)',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          {loading ? (
            <TableSkeletonLoader />
          ) : (
            <Box>
              <MasterTable
                columns={columns}
                data={labTestData ?? []}
                customTableStyle={labTests_Style.dataTableCustomSx}
                hideFooter
                rootStyle={{ height: '68vh' }}
              />
            </Box>
          )}
          <Stack
            flexDirection="row"
            justifyContent="end"
            sx={{ width: '100%' }}
          >
            <Pagination
              totalCount={labTestCount}
              onLimitChange={onLimitChange}
              onOffsetchange={onOffsetchange}
              offset={offset}
              rowsPerPage={rowsPerPage}
            />
          </Stack>
        </Grid>
      </Grid>
      <LabTestsDrawer
        drawerOpen={drawerOpen}
        closeMasterDrawer={closeDrugMasterDrawer}
        upsertMaster={upsertMaster}
      />
    </Box>
  );
}

LabTestsMaster.propTypes = {
  className: PropTypes.string,
};
